import { useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { api, type ConsignmentOverviewSeries } from "../api";
import { formatVND } from "../utils";

const PALETTE = [
  "#34d399",
  "#60a5fa",
  "#a78bfa",
  "#fbbf24",
  "#f472b6",
  "#2dd4bf",
  "#fb923c",
  "#e879f9",
  "#94a3b8", // Khác
];

const OTHER_KEY = "__other__";
const TOP_N = 8;

function truncateName(name: string, max = 18) {
  const s = (name || "").trim() || "—";
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

type SeriesLine = { key: string; name: string; color: string };

export function ConsignmentOverviewChart({ from, to }: { from: string; to: string }) {
  const [data, setData] = useState<ConsignmentOverviewSeries | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    api.consignmentItems
      .overviewSeries(from, to)
      .then((d) => {
        if (alive) setData(d);
      })
      .catch((e: unknown) => {
        if (alive) {
          setData(null);
          setError(e instanceof Error ? e.message : "Lỗi tải biểu đồ");
        }
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [from, to]);

  const { chartData, lines, leftoverNote, empty } = useMemo(() => {
    if (!data?.days?.length || !data.suppliers?.length) {
      return { chartData: [] as Record<string, string | number>[], lines: [] as SeriesLine[], leftoverNote: "", empty: true };
    }

    const totals = new Map<string, number>();
    for (const s of data.suppliers) totals.set(s.supplier_id, 0);
    for (const day of data.days) {
      for (const s of data.suppliers) {
        const v = Number(day[s.supplier_id] ?? 0) || 0;
        totals.set(s.supplier_id, (totals.get(s.supplier_id) || 0) + v);
      }
    }

    const ranked = [...data.suppliers]
      .map((s) => ({ ...s, total: totals.get(s.supplier_id) || 0 }))
      .sort((a, b) => b.total - a.total);

    const withSales = ranked.filter((s) => s.total > 0);
    const pool = withSales.length > 0 ? withSales : ranked;
    const top = pool.slice(0, TOP_N);
    const rest = pool.slice(TOP_N);
    const restSum = rest.reduce((s, r) => s + r.total, 0);
    const useOther = rest.length > 0 && restSum > 0;

    const nameById = new Map(data.suppliers.map((s) => [s.supplier_id, s.supplier_name]));
    const linesOut: SeriesLine[] = top.map((s, i) => ({
      key: s.supplier_id,
      name: truncateName(nameById.get(s.supplier_id) || s.supplier_name),
      color: PALETTE[i % (PALETTE.length - 1)],
    }));
    if (useOther) {
      linesOut.push({
        key: OTHER_KEY,
        name: `Khác (${rest.length})`,
        color: PALETTE[PALETTE.length - 1],
      });
    }

    const topIds = new Set(top.map((s) => s.supplier_id));
    const chart = data.days.map((day) => {
      const row: Record<string, string | number> = {
        date: String(day.date).slice(5), // MM-DD
        fullDate: String(day.date),
      };
      for (const id of topIds) {
        row[id] = Number(day[id] ?? 0) || 0;
      }
      if (useOther) {
        let other = 0;
        for (const r of rest) other += Number(day[r.supplier_id] ?? 0) || 0;
        row[OTHER_KEY] = other;
      }
      return row;
    });

    const hasAny = linesOut.some((l) => chart.some((d) => Number(d[l.key]) > 0));
    const leftover =
      rest.length > 0 && !useOther
        ? `còn ${rest.length} NCC`
        : rest.length > 0 && useOther
          ? `Top ${TOP_N} + Khác · còn ${rest.length} NCC gộp`
          : pool.length > TOP_N
            ? `còn ${pool.length - TOP_N} NCC`
            : "";

    return {
      chartData: chart,
      lines: linesOut,
      leftoverNote: leftover,
      empty: !hasAny && withSales.length === 0,
    };
  }, [data]);

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: "center", color: "var(--text-secondary)" }}>
        Đang tải biểu đồ…
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24, textAlign: "center", color: "var(--text-secondary)" }}>
        {error}
      </div>
    );
  }

  if (empty || lines.length === 0 || chartData.length === 0) {
    return (
      <div style={{ padding: 24, textAlign: "center", color: "var(--text-secondary)" }}>
        Chưa có doanh thu theo ngày trong kỳ
      </div>
    );
  }

  return (
    <div>
      <div style={{ height: 240 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 12, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="date" stroke="#666" fontSize={10} />
            <YAxis
              stroke="#666"
              fontSize={10}
              tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}k`}
            />
            <Tooltip
              contentStyle={{
                background: "#0a0a0a",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 10,
              }}
              labelStyle={{ color: "#888" }}
              labelFormatter={(_, payload) => {
                const full = payload?.[0]?.payload?.fullDate;
                return full ? String(full) : "";
              }}
              formatter={(v: number) => formatVND(Number(v) || 0)}
            />
            <Legend
              wrapperStyle={{ fontSize: 11 }}
              formatter={(value: string) => (
                <span style={{ color: "var(--text-secondary)" }}>{value}</span>
              )}
            />
            {lines.map((l) => (
              <Line
                key={l.key}
                type="monotone"
                dataKey={l.key}
                name={l.name}
                stroke={l.color}
                strokeWidth={l.key === OTHER_KEY ? 1.5 : 2}
                strokeDasharray={l.key === OTHER_KEY ? "6 3" : undefined}
                dot={false}
                activeDot={{ r: 4 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      {leftoverNote ? (
        <div style={{ marginTop: 6, fontSize: 11, color: "var(--text-secondary)", opacity: 0.8 }}>
          {leftoverNote}
        </div>
      ) : null}
    </div>
  );
}
